import { ensureCustomFilterStyle } from './filterStyles';

/**
 * RateRangeFilter
 *
 * 비율(%) 범위 필터 (고현율, 적현율, 고저율, 저현율 등)
 * 슬라이더와 입력 필드로 범위 조절 가능
 */
class RateRangeFilter {
  constructor() {
    this.model = null; // { min: 10, max: 50 }
    this.eGui = null;
    this.params = null;
  }

  init(params) {
    ensureCustomFilterStyle();
    this.params = params;
    this.onClose = params.onClose || null;
    this.eGui = document.createElement('div');
    this.eGui.className = 'aptrank-custom-filter';
    this.render();
  }

  getGui() {
    return this.eGui;
  }

  render() {
    const from = this.model?.min ?? '';
    const to = this.model?.max ?? '';
    // 설정값이 없으면 슬라이더는 전체 범위(min=좌측끝, max=우측끝)로 배치해 두 핸들이 겹치지 않도록.
    // Number('')는 0(유한값)이라 단순 isFinite 검사로는 빈 값 판별이 안 됨 → 빈 문자열 명시 처리.
    const sliderMin = from === '' ? 0 : Math.max(0, Math.min(500, Number(from) || 0));
    const sliderMax = to === '' ? 500 : Math.max(0, Math.min(500, Number(to) || 500));

    this.eGui.innerHTML = `
      <button type="button" class="complex-top-filter-panel-close" style="position: absolute; top: 8px; right: 8px; border: none; background: transparent; color: #64748b; width: 28px; height: 28px; border-radius: 8px; font-size: 24px; line-height: 1; cursor: pointer; z-index: 10;">×</button>
      <div class="aptrank-custom-filter-head">
        <div class="aptrank-custom-filter-heading">비율 범위 (%)</div>
      </div>
      <div class="aptrank-custom-filter-title-edit">
        <input class="title-min" inputmode="decimal" placeholder="최소 %" value="${from}" />
        <span>~</span>
        <input class="title-max" inputmode="decimal" placeholder="최대 %" value="${to}" />
      </div>
      <div class="aptrank-custom-filter-slider-wrap">
        <div class="aptrank-custom-filter-slider-track"></div>
        <input class="aptrank-custom-filter-slider min-slider" type="range" min="0" max="500" step="1" value="${Math.max(0, Math.min(500, sliderMin))}" />
        <input class="aptrank-custom-filter-slider max-slider" type="range" min="0" max="500" step="1" value="${Math.max(0, Math.min(500, sliderMax))}" />
      </div>
      <div class="aptrank-custom-filter-actions">
        <button class="aptrank-custom-filter-reset" type="button">초기화</button>
        <button class="aptrank-custom-filter-apply" type="button">적용</button>
      </div>
    `;

    this.attachEventListeners();
  }

  attachEventListeners() {
    const applyFilter = () => {
      const minRaw = this.eGui.querySelector('input.title-min').value.trim();
      const maxRaw = this.eGui.querySelector('input.title-max').value.trim();
      if (!minRaw && !maxRaw) {
        this.model = null;
      } else {
        const min = minRaw ? Number(minRaw) : null;
        const max = maxRaw ? Number(maxRaw) : null;
        this.model = { min, max };
      }
      this.params.filterChangedCallback();
    };

    const updateSlidersFromInputs = () => {
      const minInput = this.eGui.querySelector('input.title-min');
      const maxInput = this.eGui.querySelector('input.title-max');
      const minSlider = this.eGui.querySelector('input.min-slider');
      const maxSlider = this.eGui.querySelector('input.max-slider');

      const minVal = minInput.value.trim();
      const maxVal = maxInput.value.trim();

      if (minVal) {
        const minNum = Math.min(Number(minVal), 500);
        minSlider.value = minNum;
      }
      if (maxVal) {
        const maxNum = Math.min(Number(maxVal), 500);
        maxSlider.value = maxNum;
      }
    };

    const updateTrack = () => {
      const minSlider = this.eGui.querySelector('input.min-slider');
      const maxSlider = this.eGui.querySelector('input.max-slider');
      const track = this.eGui.querySelector('.aptrank-custom-filter-slider-track');
      if (!track || !minSlider || !maxSlider) return;

      const minVal = Number(minSlider.value);
      const maxVal = Number(maxSlider.value);
      const leftPercent = (minVal / 500) * 100;
      const rightPercent = 100 - (maxVal / 500) * 100;

      track.style.left = `${leftPercent}%`;
      track.style.right = `${rightPercent}%`;
    };

    // 슬라이더 변경 시 입력 필드 및 트랙 업데이트
    this.eGui.querySelector('input.min-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const maxText = this.eGui.querySelector('input.title-max').value.trim();
      const max = maxText ? Number(maxText) : 500;
      this.eGui.querySelector('input.title-min').value = String(Math.min(next, max));
      updateTrack();
    });

    this.eGui.querySelector('input.max-slider').addEventListener('input', (e) => {
      const next = Number(e.target.value);
      const minText = this.eGui.querySelector('input.title-min').value.trim();
      const min = minText ? Number(minText) : 0;
      this.eGui.querySelector('input.title-max').value = String(Math.max(next, min));
      updateTrack();
    });

    // 입력 필드 변경 시 슬라이더 및 트랙 업데이트
    this.eGui.querySelector('input.title-min').addEventListener('input', () => {
      updateSlidersFromInputs();
      updateTrack();
    });

    this.eGui.querySelector('input.title-max').addEventListener('input', () => {
      updateSlidersFromInputs();
      updateTrack();
    });

    // 초기 트랙 위치 설정
    updateTrack();

    // 적용 버튼
    this.eGui.querySelector('.aptrank-custom-filter-apply').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      applyFilter();
    });

    // 초기화 버튼
    this.eGui.querySelector('.aptrank-custom-filter-reset').addEventListener('click', (e) => {
      e.stopPropagation();
      e.preventDefault();
      this.eGui.querySelector('input.title-min').value = '';
      this.eGui.querySelector('input.title-max').value = '';
      this.eGui.querySelector('input.min-slider').value = 0;
      this.eGui.querySelector('input.max-slider').value = 500;
      updateTrack();
      this.model = null;
      this.params.filterChangedCallback();
    });

    // 닫기 버튼
    const closeBtn = this.eGui.querySelector('.complex-top-filter-panel-close');
    if (closeBtn && this.onClose) {
      closeBtn.addEventListener('click', this.onClose);
    }
  }

  getModel() {
    if (!this.model) return null;
    if (this.model.min === null && this.model.max === null) return null;
    return this.model;
  }

  setModel(model) {
    this.model = model || null;
    if (this.eGui) this.render();
  }

  isFilterActive() {
    return this.model !== null && (this.model.min !== null || this.model.max !== null);
  }

  doesFilterPass() {
    // AG-Grid에서 사용하지 않음 (서버 필터링)
    return true;
  }
}

export default RateRangeFilter;
